import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, TrendingUp, CircleDollarSign, RefreshCw, ExternalLink } from "lucide-react";
import { formatINR } from "../lib/format";
import { useAuth } from "../context/AuthContext";

const LEVEL2 = new Set(["introducer", "custodian", "guarantor"]);

/**
 * Live-position portfolio for a Level-2 sponsor's dependents:
 * open orders, pending claims and month-to-date earnings — side by side.
 * Renders nothing for non-sponsors.
 */
export const SponsorDependentsPortfolio = () => {
  const { user, api } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, sum] = await Promise.all([
        api.get("/sponsor/dashboard"),
        api.get("/sponsor/approvals/summary"),
      ]);
      setRows(dash.data?.dependents || []);
      setSummary(sum.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load portfolio"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { if (LEVEL2.has(user?.buyer_category)) load(); }, [user, load]);
  if (!user || !LEVEL2.has(user.buyer_category)) return null;

  return (
    <div data-testid="sponsor-portfolio" className="mt-14">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Sponsor Portfolio</div>
          <h2 className="font-serif text-2xl md:text-3xl mt-1 flex items-center gap-3">
            <Users size={20} className="text-[#D8B23E]"/>
            Your dependents · live position
          </h2>
        </div>
        <button data-testid="sponsor-portfolio-refresh"
                onClick={load}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/12 hover:border-[#D8B23E]/50 px-4 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#8A8A82] hover:text-[#F5F5F0]">
          <RefreshCw size={12}/> Refresh
        </button>
      </div>

      {/* Unified sponsor-approvals counts */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            { k: "Claims", v: summary.pending_claims, testid: "sum-claims", color: "#B87333" },
            { k: "Orders", v: summary.pending_orders, testid: "sum-orders", color: "#2FB999" },
            { k: "Deals", v: summary.pending_deals, testid: "sum-deals", color: "#D8B23E" },
            { k: "KYC changes", v: summary.pending_kyc, testid: "sum-kyc", color: "#8AA9FF" },
            { k: "Total", v: summary.total_pending, testid: "sum-total", color: "#F5F5F0" },
          ].map(({ k, v, testid, color }) => (
            <div key={k} data-testid={testid}
                 className="border border-white/10 rounded-sm bg-[#0a0a0a] p-3">
              <div className="font-grotesk text-[9px] uppercase tracking-[0.22em]" style={{ color }}>{k}</div>
              <div className="font-serif text-2xl mt-1 tabular-nums text-[#F5F5F0]">{v}</div>
              <div className="font-body text-[10px] text-[#8A8A82]">pending</div>
            </div>
          ))}
        </div>
      )}

      {loading && <div className="p-6 font-body text-xs text-[#8A8A82]">Loading portfolio…</div>}
      {!loading && rows.length === 0 && (
        <div className="border border-dashed border-white/12 rounded-sm p-8 text-center">
          <p className="font-body text-sm text-[#C9C9C2]">No dependents mapped under your account yet.</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((d) => (
          <div key={d.user_id} data-testid={`dep-card-${d.user_id}`}
               className="border border-white/10 rounded-sm bg-[#0a0a0a] p-4 hover:border-[#D8B23E]/40 transition-colors relative">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-serif text-base text-[#F5F5F0] truncate">{d.business_name || d.email}</div>
                <div className="font-body text-[11px] text-[#8A8A82] truncate">{d.email} · {d.phone || "—"}</div>
                <div className="font-grotesk text-[9px] uppercase tracking-[0.18em] text-[#B87333] mt-1">
                  {d.category?.replace(/_/g, " ")} · {d.vendor_code || "no code"}
                </div>
              </div>
              {d.verified ? (
                <span className="font-grotesk text-[8px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border border-[#2FB999]/40 text-[#2FB999] bg-[#2FB999]/[0.05]">Verified</span>
              ) : (
                <span className="font-grotesk text-[8px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border border-white/15 text-[#8A8A82]">{d.kyc_status || "pending"}</span>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-white/[0.06]">
              <div>
                <div className="font-grotesk text-[8px] uppercase tracking-[0.18em] text-[#8A8A82]"><TrendingUp size={9} className="inline mr-1"/>Open orders</div>
                <div className="font-serif text-lg tabular-nums text-[#F5F5F0]">{d.open_orders}</div>
              </div>
              <div>
                <div className="font-grotesk text-[8px] uppercase tracking-[0.18em] text-[#8A8A82]">Pending claims</div>
                <div className="font-serif text-lg tabular-nums text-[#B87333]">{d.pending_claims}</div>
              </div>
              <div>
                <div className="font-grotesk text-[8px] uppercase tracking-[0.18em] text-[#8A8A82]"><CircleDollarSign size={9} className="inline mr-1"/>MTD ₹</div>
                <div className="font-serif text-lg tabular-nums text-[#2FB999]">{formatINR(d.mtd_earnings_inr).replace("₹","")}</div>
              </div>
            </div>
            <a href={`mailto:${d.email}`} data-testid={`dep-contact-${d.user_id}`}
               className="inline-flex items-center gap-1 mt-3 font-grotesk text-[9px] uppercase tracking-[0.18em] text-[#8A8A82] hover:text-[#D8B23E]">
              Contact <ExternalLink size={10}/>
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};
